import { cond } from 'lodash';

import { GridColDef, GridColumnsInitialState } from '@mui/x-data-grid';
import { GridInitialStateCommunity } from '@mui/x-data-grid/models/gridStateCommunity';

import { Tooltip, Typography } from '../components/MuiGenerals';
import { useAlerting, useTranslation } from '../hooks';
import { copyToClipboard } from '../utils';
import { useZusTranslatePrefixStore } from '../zustand/store';
import { tableConfig } from './constant';
import { styled } from '@mui/material';


const StyledTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isCopyable',
})<{ isCopyable: boolean }>(({ theme, isCopyable }) => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',

  ...(isCopyable && {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    textDecoration: 'underline',
  }),
}));

const sx = {
  label: {
    padding: '25px 0px 0px 0px',
  },
};

export function useGenFilterField(
  array: [string, any][],
  config?: { translate?: (key: string) => string }
) {
  const { translatePrefix } = useZusTranslatePrefixStore();

  const { t } = useTranslation(translatePrefix);

  const fieldArr = array.map(([label, content]: any) => {
    const displayLabel = (config?.translate && config?.translate(label)) || t(label);

    return (
      <>
        <Typography variant="subtitle2" sx={sx.label}>
          {displayLabel}
        </Typography>
        <div className="filterFieldInput">{content}</div>
      </>
    );
  });

  return fieldArr;
}

export function useGenGridCol(field: string, rest?: Partial<GridColDef>): GridColDef {
  const { translatePrefix } = useZusTranslatePrefixStore();  
  const { t, tc } = useTranslation(translatePrefix);
  const { alerting } = useAlerting();
  const isDateField = (field: string) => tableConfig.dateFields.includes(field);
  const defaultCase = () => true;
  const isCopyable = (field: string) => tableConfig.copyableFields.includes(field);
  const isFreeTextField = (field: string) => tableConfig.freeTextFields.includes(field);
  const renderCell = (params: any) => {
    const { value = '', field = '' } = params;
    const isBoolean = (v: any) => typeof v === 'boolean';
    const content = cond([
      [(v: any) => v === '0', () => '0'],
      [(v: any) => v === 0, () => 0],
      [(v: any) => !v, () => ''],
      [isBoolean, (v: any) => (v ? 'yes' : 'no')],
      [defaultCase, (v: any) => v],
    ])(value);
    return (
      <Tooltip title={content}>
        <StyledTypography
          variant="body2"
          isCopyable={isCopyable(field)}
          className="csutable-cell-trucate"
          onClick={(e) => cellOnClick(e, field, value)}
        >
          {content}
        </StyledTypography>
      </Tooltip>
    );
  };
  const minWidth = cond([
    [isCopyable, () => tableConfig.addressWidth],
    [isDateField, () => tableConfig.dateWidth],
    [isFreeTextField, () => tableConfig.freeTextWidth],
    [defaultCase, () => tableConfig.defaultWidth],
  ])(field);

  const cellOnClick = (e: any, field: string, value: string) => {
    if (isCopyable(field)) {
      copyToClipboard(value);
      alerting('success', tc(`copied`));
    }
  };
  return {
    field,
    headerName: t(field),
    flex: 1,
    minWidth,
    renderCell,
    headerAlign: 'center',
    align: 'center',
    sortable: false,
    ...rest,
  };
}

export const dataGridColumnConfig = (
  visibilityConfigArr?: { fieldName: string; hasPermission: boolean }[],
  otherColumnsConfig?: Omit<GridColumnsInitialState, 'columnVisibilityModel'>
): GridInitialStateCommunity => {
  const columnVisibilityModel = visibilityConfigArr?.reduce(
    (prev: Record<string, boolean>, { fieldName, hasPermission }) => ({
      ...prev,
      [fieldName]: hasPermission,
    }),
    {}
  );

  return {
    columns: {
      columnVisibilityModel,
      ...otherColumnsConfig,
    },
  };
};
